import * as React from 'react';
import BPromise from 'bluebird';
import Lazy from 'lazy.js';
import propz from 'propz';
import { MonthYearSelector } from './MonthYearSelector';
import { Component } from 'react';
import moment from 'moment';
import { DEFAULT_RANGE_CALENDAR_YEARS } from '../../../../consts/calendar';
import {
  getFilterClubEventDatesForMonth,
  getFilterClubEventsForDay,
  getFilterEventDatesForMonth,
  getFilterEventsForDay,
  getFilterFixtureEventDatesForMonth,
  getFilterFixtureEventsForDay,
  getFilterTournamentDatesForMonth,
  getFilterTournamentEventDatesForMonth,
  getFilterTournamentEventsForDay,
  getFilterTournamentsForDay,
  getYearRangeArray,
  isAllFilter,
  isClubEventsFilter,
  isSportEventsFilter,
  isTournamentEventsFilter,
  isTournamentsFilter
} from '../../../../helpers/calendar/calendar';
import { DaysOfWeekBar } from './DaysOfWeekBar';
import { MonthDaysPanel } from './MonthDaysPanel';
import './Calendar.scss';
import { SchoolEvent } from '../../../../models/event';
import { Loader } from '../../../../components/Loader/Loader';
import { AppUser } from '../../../App/App';
import { History, Location } from 'history';
import { getSchoolEvents } from 'Src/helpers/service/admin/schoolEvents';
import { EventAndTournamentsTable } from './EventAndTournamentsTable';
import { getSchoolEventDates } from 'Src/helpers/service/admin/calendar';
import { CalendarFilterButtons } from './CalendarFilterButtons';
import { getSchoolTournamentDates, getSchoolTournaments } from 'Src/helpers/service/admin/tournaments';
import { Tournament } from '../../../../models/tournament';
import { Button } from '../../../../components/Button/Button';

export type FilterType = 'TOURNAMENTS' | 'SPORT_EVENTS' | 'CLUB_EVENTS' | 'TOURNAMENT_EVENTS' | 'ALL';

export const CALENDAR_FILTER = {
  SPORT_EVENTS: 'SPORT_EVENTS',
  CLUB_EVENTS: 'CLUB_EVENTS',
  TOURNAMENT_EVENTS: 'TOURNAMENT_EVENTS',
  TOURNAMENTS: 'TOURNAMENTS',
  ALL: 'ALL'
};

type CalendarDateType = 'fixture' | 'club' | 'tournamentEvent' | 'tournament';
type CalendarDateTypes = Record<string, CalendarDateType[]>;

function getCalendarDateKey(value: string | Date): string {
  const date = new Date(value);
  return date.getFullYear() + '-' + date.getMonth() + '-' + date.getDate();
}

function addCalendarDateTypes(map: CalendarDateTypes, values: string[], type: CalendarDateType): void {
  values.forEach(value => {
    const key = getCalendarDateKey(value);
    const current = map[key] || [];
    current.push(type);
    map[key] = current;
  });
}

function buildCalendarDateState(fixtureEventDates, clubEventDates, tournamentEventDates, tournamentDates) {
  const dates = [
    ...fixtureEventDates.dates,
    ...clubEventDates.dates,
    ...tournamentEventDates.dates,
    ...tournamentDates.dates
  ];
  const eventDateTypes: CalendarDateTypes = {};

  addCalendarDateTypes(eventDateTypes, fixtureEventDates.dates, 'fixture');
  addCalendarDateTypes(eventDateTypes, clubEventDates.dates, 'club');
  addCalendarDateTypes(eventDateTypes, tournamentEventDates.dates, 'tournamentEvent');
  addCalendarDateTypes(eventDateTypes, tournamentDates.dates, 'tournament');

  const datesUniq: string[] = Lazy(dates)
    .uniq()
    .toArray();

  return {
    dates: datesUniq.map(date => new Date(date)),
    eventDateTypes
  };
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface State {
  monthAndYearDate: Date;
  selectedDate: Date;
  nowDate: Date;
  fixtureEvents: SchoolEvent[];
  clubEvents: SchoolEvent[];
  tournamentEvents: SchoolEvent[];
  tournaments: Tournament[];
  dates: Date[];
  eventDateTypes: CalendarDateTypes;
  activeMonthIndex: number;
  activeYearIndex: number;
  isLoading: boolean;
  filter: FilterType;
}

export class Calendar extends Component<Props, State> {
  constructor(props) {
    super(props);

    const now = new Date();
    const nowMonth = now.getMonth();
    const nowYear = now.getFullYear();
    const monthAndYearDate = new Date(nowYear, nowMonth, 1);

    this.state = {
      monthAndYearDate,
      selectedDate: now,
      nowDate: now,
      activeMonthIndex: nowMonth,
      activeYearIndex: DEFAULT_RANGE_CALENDAR_YEARS,
      fixtureEvents: [],
      clubEvents: [],
      tournamentEvents: [],
      dates: [],
      eventDateTypes: {},
      tournaments: [],
      isLoading: false,
      filter: CALENDAR_FILTER.ALL as FilterType
    };
  }

  getEventPromises(filter: FilterType, monthAndYearDate: Date, selectedDate: Date) {
    const { user } = this.props;

    const filterEventDates = getFilterEventDatesForMonth(monthAndYearDate, user);
    const filterEvents = getFilterEventsForDay(selectedDate, user);

    const filterSportEventDates = getFilterFixtureEventDatesForMonth(filterEventDates);
    const filterSportEvents = getFilterFixtureEventsForDay(filterEvents);

    const filterClubEventDates = getFilterClubEventDatesForMonth(filterEventDates);
    const filterClubEvents = getFilterClubEventsForDay(filterEvents);

    const filterTournamentEventDates = getFilterTournamentEventDatesForMonth(filterEventDates);
    const filterTournamentEvents = getFilterTournamentEventsForDay(filterEvents);

    const filterTournamentDates = getFilterTournamentDatesForMonth(monthAndYearDate);
    const filterTournaments = getFilterTournamentsForDay(selectedDate);

    const isAllOrSportEventsFilter = isAllFilter(filter) || isSportEventsFilter(filter);
    const isAllOrClubEventsFilter = isAllFilter(filter) || isClubEventsFilter(filter);
    const isAllOrTournamentEventsFilter = isAllFilter(filter) || isTournamentEventsFilter(filter);
    const isAllOrTournamentsFilter = isAllFilter(filter) || isTournamentsFilter(filter);

    //TODO: Use it for teacher/coach
    //not show tournaments and tournament events for teacher/coach
    // const isUserCoachOrTeacher = isUserCoach(user) || isUserTeacher(user);
    // return [
    //   isAllOrSportEventsFilter ? getSchoolEventDates(user, filterSportEventDates) : BPromise.resolve({ dates: [] }),
    //   isAllOrSportEventsFilter ? getSchoolEvents(user, filterSportEvents) : BPromise.resolve([]),
    //   isAllOrClubEventsFilter ? getSchoolEventDates(user, filterClubEventDates) : BPromise.resolve({ dates: [] }),
    //   isAllOrClubEventsFilter ? getSchoolEvents(user, filterClubEvents) : BPromise.resolve([]),
    //   isAllOrTournamentEventsFilter && !isUserCoachOrTeacher
    //     ? getSchoolEventDates(user, filterTournamentEventDates)
    //     : BPromise.resolve({ dates: [] }),
    //   isAllOrTournamentEventsFilter && !isUserCoachOrTeacher
    //     ? getSchoolEvents(user, filterTournamentEvents)
    //     : BPromise.resolve([]),
    //   isAllOrTournamentsFilter && !isUserCoachOrTeacher
    //     ? getSchoolTournamentDates(user, filterTournamentDates)
    //     : BPromise.resolve({ dates: [] }),
    //   isAllOrTournamentsFilter && !isUserCoachOrTeacher
    //     ? getSchoolTournaments(user, filterTournaments)
    //     : BPromise.resolve([])
    // ];

    return [
      isAllOrSportEventsFilter ? getSchoolEventDates(user, filterSportEventDates) : BPromise.resolve({ dates: [] }),
      isAllOrSportEventsFilter ? getSchoolEvents(user, filterSportEvents) : BPromise.resolve([]),
      isAllOrClubEventsFilter ? getSchoolEventDates(user, filterClubEventDates) : BPromise.resolve({ dates: [] }),
      isAllOrClubEventsFilter ? getSchoolEvents(user, filterClubEvents) : BPromise.resolve([]),
      isAllOrTournamentEventsFilter
        ? getSchoolEventDates(user, filterTournamentEventDates)
        : BPromise.resolve({ dates: [] }),
      isAllOrTournamentEventsFilter ? getSchoolEvents(user, filterTournamentEvents) : BPromise.resolve([]),
      isAllOrTournamentsFilter
        ? getSchoolTournamentDates(user, filterTournamentDates)
        : BPromise.resolve({ dates: [] }),
      isAllOrTournamentsFilter ? getSchoolTournaments(user, filterTournaments) : BPromise.resolve([])
    ];
  }

  componentDidMount() {
    const { location } = this.props;
    const { monthAndYearDate, selectedDate, filter } = this.state;
    const { state } = location;

    const filterFromHistory = propz.get(state, ['filter']);
    const selectedDateFromHistory = propz.get(state, ['selectedDate']);
    const isFilterFromHistoryExist = typeof filterFromHistory !== 'undefined';
    const isSelectedDateFromHistoryExist = typeof selectedDateFromHistory !== 'undefined';

    const currentFilter = isFilterFromHistoryExist ? filterFromHistory : filter;
    const currentDate = isSelectedDateFromHistoryExist ? selectedDateFromHistory : selectedDate;

    const now = new Date(currentDate);
    const nowMonth = now.getMonth();
    const nowYear = now.getFullYear();
    const currentMonthAndYearDate = new Date(nowYear, nowMonth, 1);

    this.setState({
      isLoading: true
    });

    const promises = this.getEventPromises(currentFilter, currentMonthAndYearDate, now);

    BPromise.all(promises).then(
      ([
        fixtureEventDates,
        fixtureEvents,
        clubEventDates,
        clubEvents,
        tournamentEventDates,
        tournamentEvents,
        tournamentDates,
        tournaments
      ]) => {
        const calendarDateState = buildCalendarDateState(
          fixtureEventDates,
          clubEventDates,
          tournamentEventDates,
          tournamentDates
        );
        this.setState({
          isLoading: false,
          ...calendarDateState,
          fixtureEvents,
          clubEvents,
          tournamentEvents,
          tournaments,
          filter: currentFilter,
          selectedDate: now,
          monthAndYearDate: currentMonthAndYearDate,
          activeMonthIndex: nowMonth
        });
      }
    );
  }

  onNextMonthClick = (): void => {
    const { monthAndYearDate, activeYearIndex, filter, selectedDate } = this.state;
    const nowMonth = monthAndYearDate.getMonth();
    const nextMonth = new Date(monthAndYearDate);
    nextMonth.setMonth(nowMonth + 1);
    const nextMonthIndex = nextMonth.getMonth();

    const promises = this.getEventPromises(filter, nextMonth, selectedDate);

    this.setState({
      isLoading: true
    });

    if (nowMonth === 11) {
      if (activeYearIndex === DEFAULT_RANGE_CALENDAR_YEARS * 2) {
        this.setState({
          isLoading: false
        });
        return;
      }

      BPromise.all(promises).then(
        ([
          fixtureEventDates,
          fixtureEvents,
          clubEventDates,
          clubEvents,
          tournamentEventDates,
          tournamentEvents,
          tournamentDates,
          tournaments
        ]) => {
          const calendarDateState = buildCalendarDateState(
            fixtureEventDates,
            clubEventDates,
            tournamentEventDates,
            tournamentDates
          );
          this.setState({
            isLoading: false,
            ...calendarDateState,
            monthAndYearDate: nextMonth,
            activeMonthIndex: nextMonthIndex,
            activeYearIndex: activeYearIndex + 1
          });
        }
      );
    } else {
      BPromise.all(promises).then(
        ([
          fixtureEventDates,
          fixtureEvents,
          clubEventDates,
          clubEvents,
          tournamentEventDates,
          tournamentEvents,
          tournamentDates,
          tournaments
        ]) => {
          const calendarDateState = buildCalendarDateState(
            fixtureEventDates,
            clubEventDates,
            tournamentEventDates,
            tournamentDates
          );
          this.setState({
            isLoading: false,
            ...calendarDateState,
            monthAndYearDate: nextMonth,
            activeMonthIndex: nextMonthIndex
          });
        }
      );
    }
  };
  onPreviousMonthClick = (): void => {
    const { monthAndYearDate, activeYearIndex, filter, selectedDate } = this.state;
    const nowMonth = monthAndYearDate.getMonth();
    const previousMonth = new Date(monthAndYearDate);
    previousMonth.setMonth(nowMonth - 1);
    const previousMonthIndex = previousMonth.getMonth();

    const promises = this.getEventPromises(filter, previousMonth, selectedDate);

    this.setState({
      isLoading: true
    });

    if (nowMonth === 0) {
      if (activeYearIndex === 0) {
        this.setState({
          isLoading: false
        });
        return;
      }

      BPromise.all(promises).then(
        ([
          fixtureEventDates,
          fixtureEvents,
          clubEventDates,
          clubEvents,
          tournamentEventDates,
          tournamentEvents,
          tournamentDates,
          tournaments
        ]) => {
          const calendarDateState = buildCalendarDateState(
            fixtureEventDates,
            clubEventDates,
            tournamentEventDates,
            tournamentDates
          );
          this.setState({
            isLoading: false,
            ...calendarDateState,
            monthAndYearDate: previousMonth,
            activeMonthIndex: previousMonthIndex,
            activeYearIndex: activeYearIndex - 1
          });
        }
      );
    } else {
      BPromise.all(promises).then(
        ([
          fixtureEventDates,
          fixtureEvents,
          clubEventDates,
          clubEvents,
          tournamentEventDates,
          tournamentEvents,
          tournamentDates,
          tournaments
        ]) => {
          const calendarDateState = buildCalendarDateState(
            fixtureEventDates,
            clubEventDates,
            tournamentEventDates,
            tournamentDates
          );
          this.setState({
            isLoading: false,
            ...calendarDateState,
            monthAndYearDate: previousMonth,
            activeMonthIndex: previousMonthIndex
          });
        }
      );
    }
  };

  onMonthYearChange = (month: number, year: number): void => {
    const { filter } = this.state;
    const nextMonth = new Date(year, month, 1);
    const promises = this.getEventPromises(filter, nextMonth, nextMonth);
    const activeYearIndex = getYearRangeArray().findIndex(option => option === year);

    this.setState({ isLoading: true });

    BPromise.all(promises).then(
      ([
        fixtureEventDates,
        fixtureEvents,
        clubEventDates,
        clubEvents,
        tournamentEventDates,
        tournamentEvents,
        tournamentDates,
        tournaments
      ]) => {
        const calendarDateState = buildCalendarDateState(
          fixtureEventDates,
          clubEventDates,
          tournamentEventDates,
          tournamentDates
        );
        this.setState({
          isLoading: false,
          ...calendarDateState,
          selectedDate: nextMonth,
          monthAndYearDate: nextMonth,
          activeMonthIndex: month,
          activeYearIndex
        });
      }
    );
  };

  onMonthChange = (value: number): void => {
    const { monthAndYearDate, filter, selectedDate } = this.state;
    const nextMonth = new Date(monthAndYearDate);
    nextMonth.setMonth(value);

    const promises = this.getEventPromises(filter, nextMonth, selectedDate);

    this.setState({
      isLoading: true
    });

    BPromise.all(promises).then(
      ([
        fixtureEventDates,
        fixtureEvents,
        clubEventDates,
        clubEvents,
        tournamentEventDates,
        tournamentEvents,
        tournamentDates,
        tournaments
      ]) => {
        const calendarDateState = buildCalendarDateState(
          fixtureEventDates,
          clubEventDates,
          tournamentEventDates,
          tournamentDates
        );
        this.setState({
          isLoading: false,
          ...calendarDateState,
          monthAndYearDate: nextMonth,
          activeMonthIndex: Number(value)
        });
      }
    );
  };

  onYearChange = (value: number): void => {
    const { monthAndYearDate, filter, selectedDate } = this.state;
    const nextYear = new Date(monthAndYearDate);
    nextYear.setFullYear(value);

    const promises = this.getEventPromises(filter, nextYear, selectedDate);

    const yearRangeArray = getYearRangeArray();
    const activeYearIndex = yearRangeArray.findIndex(year => year === Number(value));

    this.setState({
      isLoading: true
    });

    BPromise.all(promises).then(
      ([
        fixtureEventDates,
        fixtureEvents,
        clubEventDates,
        clubEvents,
        tournamentEventDates,
        tournamentEvents,
        tournamentDates,
        tournaments
      ]) => {
        const calendarDateState = buildCalendarDateState(
          fixtureEventDates,
          clubEventDates,
          tournamentEventDates,
          tournamentDates
        );
        this.setState({
          isLoading: false,
          ...calendarDateState,
          monthAndYearDate: nextYear,
          activeYearIndex: activeYearIndex
        });
      }
    );
  };

  onDayClick = (date: Date): void => {
    const { filter, monthAndYearDate } = this.state;
    this.setState({
      isLoading: true
    });

    const promises = this.getEventPromises(filter, monthAndYearDate, date);

    BPromise.all(promises).then(
      ([
        fixtureEventDates,
        fixtureEvents,
        clubEventDates,
        clubEvents,
        tournamentEventDates,
        tournamentEvents,
        tournamentDates,
        tournaments
      ]) => {
        this.setState({
          selectedDate: date,
          isLoading: false,
          fixtureEvents,
          clubEvents,
          tournamentEvents,
          tournaments
        });
      }
    );
  };

  onFilterButtonClick = (filter: FilterType) => {
    const { monthAndYearDate, selectedDate } = this.state;

    this.setState({
      isLoading: true
    });

    const promises = this.getEventPromises(filter, monthAndYearDate, selectedDate);

    BPromise.all(promises).then(
      ([
        fixtureEventDates,
        fixtureEvents,
        clubEventDates,
        clubEvents,
        tournamentEventDates,
        tournamentEvents,
        tournamentDates,
        tournaments
      ]) => {
        const calendarDateState = buildCalendarDateState(
          fixtureEventDates,
          clubEventDates,
          tournamentEventDates,
          tournamentDates
        );
        this.setState({
          isLoading: false,
          ...calendarDateState,
          fixtureEvents,
          clubEvents,
          tournamentEvents,
          tournaments,
          filter
        });
      }
    );
  };

  onCreateEventClick = (event, selectedDate) => {
    event.preventDefault();
    const createDate = moment(selectedDate).format('DD-MM-YYYY');

    this.props.history.push({
      pathname: '/events/manager',
      search: `date=${createDate}`
    });
  };

  onEventClick = (eventId: string): void => {
    const { location } = this.props;
    const { pathname } = location;
    const { filter } = this.state;

    this.props.history.push({
      pathname: '/events/event',
      search: `id=${eventId}`,
      state: {
        filter,
        prevRoutePath: pathname
      }
    });
  };

  render() {
    const {
      activeMonthIndex,
      activeYearIndex,
      selectedDate,
      monthAndYearDate,
      nowDate,
      isLoading,
      fixtureEvents,
      clubEvents,
      tournamentEvents,
      tournaments,
      dates,
      eventDateTypes,
      filter
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const { user } = this.props;

    const events = [...fixtureEvents, ...clubEvents, ...tournamentEvents];
    const eventsSorted = events.sort((event1, event2) => {
      const eventStartTimeDate1 = new Date(event1.startTime);
      const eventStartTimeDate2 = new Date(event2.startTime);
      return Number(eventStartTimeDate1) - Number(eventStartTimeDate2);
    });
    const selectedDateTitle = moment(selectedDate).format('D MMMM YYYY');
    const selectedDateRelative = moment(selectedDate).isSame(nowDate, 'day')
      ? 'Today'
      : moment(selectedDate).format('dddd');
    const selectedItemsCount = eventsSorted.length + tournaments.length;

    return (
      <div className="cal-screen cal-screen--admin">
        <header className="cal-toolbar">
          <div>
            <h1 className="cal-toolbar__title">Calendar</h1>
          </div>
          <Button
            key={'create_events'}
            customClass={'cal-create'}
            onClick={e => {
              this.onCreateEventClick(e, selectedDate);
            }}
            text={'+ Create event'}
          />
        </header>

        <CalendarFilterButtons user={user} filter={filter} onFilterButtonClick={this.onFilterButtonClick} />

        <div className="cal">
          <section className="cal-card cal-card--month">
            <MonthYearSelector
              activeMonthIndex={activeMonthIndex}
              activeYearIndex={activeYearIndex}
              onPreviousMonthClick={this.onPreviousMonthClick}
              onNextMonthClick={this.onNextMonthClick}
              onMonthYearChange={this.onMonthYearChange}
            />
            <DaysOfWeekBar />
            <MonthDaysPanel
              onClick={this.onDayClick}
              selectedDate={selectedDate}
              monthAndYearDate={monthAndYearDate}
              nowDate={nowDate}
              eventDates={dates}
              eventDateTypes={eventDateTypes}
            />
          </section>

          <section className="cal-card cal-card--events">
            <div className="cal-day__hd">
              <div>
                <h2 className="cal-day__date">{selectedDateTitle}</h2>
                <span className="cal-day__rel">· {selectedDateRelative}</span>
              </div>
              {selectedItemsCount > 0 ? (
                <span className="cal-day__count">
                  {selectedItemsCount} event{selectedItemsCount === 1 ? '' : 's'}
                </span>
              ) : null}
            </div>
            <div className="eEventAndTournamentsTableSection">
              <EventAndTournamentsTable
                user={user}
                events={eventsSorted}
                tournaments={tournaments}
                onEventClick={this.onEventClick}
              />
            </div>
          </section>
        </div>
      </div>
    );
  }
}
